import React from "react";
import Image from "next/image";
import { FUNDAMENTAL_TABS, SimilerStock } from "@/app/interface/global";
import { /* formatAmountToK, */ formatCurrency } from "@/app/utils/currency-formatter";
import { CountryCurrency } from "@/app/interface/currency";
import { cn } from "@/lib/utils";
// import { formatAmountToUS } from "@/app/utils/currency-formatter";
import Link from "next/link";

interface Props {
    similarStocks: SimilerStock[] | undefined;
    culture: CountryCurrency;
    partialLinkUrl?: string | boolean;
}

function PerformanceIndicator({ isPositive }: { isPositive: boolean | string }) {
    if (isPositive === "--") return null;

    return <span className="ml-1 text-xs leading-none">{isPositive ? "▲" : "▼"}</span>;
}

const SimilerStocks: React.FC<Props> = ({ similarStocks, culture, partialLinkUrl }) => {
    if (!similarStocks) {
        return <p className="text-sm italic text-gray-500">No similer stocks found</p>;
    }

    return (
        <div className="mt-6 w-full">
            {" "}
            {/* max-w-[812px] */}
            <h2 className="mb-6 text-3xl font-semibold xs:text-xl">
                {FUNDAMENTAL_TABS.SIMILAR_STOCKS}
            </h2>
            <div className="space-y-8 overflow-x-auto rounded-xl border border-[#0E0F171A]">
                <table className="w-full md:min-w-[700px]">
                    <thead>
                        <tr className="text-left">
                            <th className="rounded-tl-xl bg-[#F4F5F9] px-5 py-3 xs:py-5 md:w-[50%]">
                                Company
                            </th>
                            <th className="bg-[#F4F5F9] px-4 py-3 xs:py-5 md:w-[30%]">Price</th>
                            {/* <th className="bg-[#F4F5F9] px-4 py-3 xs:py-5 md:w-[25%]">
                                Market cap
                            </th> */}
                            {/* <th className="w-[150px] bg-[#F4F5F9] py-3 xs:py-5">Returns(5Y)</th>
                            <th className="w-[120px] rounded-tr-xl bg-[#F4F5F9] py-3 xs:py-5">
                                PE ratio
                            </th> */}
                        </tr>
                    </thead>
                    <tbody className="text-sm md:text-base">
                        {similarStocks?.length > 0 ? (
                            similarStocks?.map((stock, index) => {
                                const isUp = stock.price_movement.one_day
                                    ? stock.price_movement.one_day > 0
                                        ? true
                                        : stock.price_movement.one_day < 0
                                          ? false
                                          : "--"
                                    : "--";
                                const colorPlus = "text-emerald-600";
                                const colorMunis = "text-red-600";

                                return (
                                    <tr key={index} className="align-top">
                                        <td className="px-5 py-3 pr-6 xs:py-6">
                                            <Link
                                                href={`${(partialLinkUrl ? partialLinkUrl : "") + stock.symbol}/${stock.company_name?.toLowerCase().replace(/\s+/g, "-")}`}
                                                className="flex items-center gap-3"
                                            >
                                                <div
                                                    className={`flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-sm border border-[#E8E9ED] bg-white`}
                                                >
                                                    {stock.logo ? (
                                                        <Image
                                                            src={stock.logo}
                                                            alt={`${stock.company_name} logo`}
                                                            width={34}
                                                            height={34}
                                                            className="h-full w-full object-contain"
                                                        />
                                                    ) : (
                                                        <span className="flex h-10 w-10 items-center justify-center text-xs font-semibold uppercase text-[#6B7280]">
                                                            {stock.symbol?.slice(0, 1)}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="min-w-0">
                                                    <span className="block text-nowrap font-semibold text-[#0F1016] md:text-wrap">
                                                        {stock.symbol}
                                                    </span>
                                                    <span className="mt-0.5 block text-xs font-medium uppercase leading-[160%] tracking-[1%] text-[#6B7280]">
                                                        {stock.company_name}
                                                    </span>
                                                </div>
                                            </Link>
                                        </td>
                                        <td className="px-4 py-3 align-middle xs:py-6 md:align-top">
                                            <div className="flex h-full flex-col md:h-auto md:justify-center">
                                                <div className="text-base font-semibold">
                                                    {formatCurrency(
                                                        stock.current_price,
                                                        culture.currencyCode,
                                                        culture.locale,
                                                    )}
                                                </div>
                                                <div
                                                    className={cn(
                                                        "flex items-center whitespace-nowrap text-xs font-normal tracking-wide md:text-base",
                                                        isUp === true
                                                            ? colorPlus
                                                            : isUp === false
                                                              ? colorMunis
                                                              : "",
                                                    )}
                                                >
                                                    {isUp === true
                                                        ? "+"
                                                        : isUp === false
                                                          ? "-"
                                                          : ""}

                                                    {isUp !== "--"
                                                        ? `${culture.symbol}${Math.abs(stock.price_movement.one_day).toFixed(2)} (${stock.price_movement.one_day_percentage?.toFixed(2)}%)`
                                                        : ""}

                                                    <PerformanceIndicator
                                                        isPositive={
                                                            isUp === true
                                                                ? true
                                                                : isUp === false
                                                                  ? false
                                                                  : isUp
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                        {/* <td className="px-4 py-3 font-semibold xs:py-6">
                                            {formatAmountToUS(stock.market_cap, culture.symbol)}
                                        </td> */}
                                        {/* <td className="py-3 xs:py-6">
                                        <div
                                            className={`flex items-center gap-1 ${stock.five_y_returns > 0 ? colorPlus : colorMunis}`}
                                        >
                                            {`${stock.five_y_returns > 0 ? "+" : "-"}${stock.five_y_returns}%`}
                                        </div>
                                    </td>
                                    <td className="py-3 xs:py-6">{stock.pe_ratio}</td> */}
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={100} className="relative p-10 text-center text-sm">
                                    <div className="flex h-full w-full items-center">
                                        <span className="absolute left-0 inline-block w-[calc(100vw-48px)] text-center md:relative md:w-full">
                                            No stocks found
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default SimilerStocks;
